/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: QueueProfileCallerExperienceSection.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the Queue Profiles module.
----------------------------------------------------------------*/

import { Volume2 } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import { FormBox } from '../../components/ui/FormBox';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import Toggle from '../../components/ui/Toggle';
import SimpleTooltip from '../../components/ui/SimpleTooltip';
import { QueueProfileFormState } from '../queueProfiles/types';

interface QueueProfileCallerExperienceSectionProps {
  formData: QueueProfileFormState;
  onFieldChange: <K extends keyof QueueProfileFormState>(field: K, value: QueueProfileFormState[K]) => void;
}

export function QueueProfileCallerExperienceSection({
  formData,
  onFieldChange
}: QueueProfileCallerExperienceSectionProps) {
  const { t } = useI18n();

  return (
    <FormBox title={t('queueProfiles.form.sections.callerExperience')} icon={<Volume2 className="h-5 w-5" />}>
      <div className="grid grid-cols-12 gap-3">
        {/* Caller Exit Destination */}
        <SimpleTooltip content={t('queueProfiles.form.fields.callerExitDestination.tooltip')} className="col-span-12 md:col-span-6 lg:col-span-3">
          <Label htmlFor="callerExitDestination" className="text-left">
            {t('queueProfiles.form.fields.callerExitDestination.label')}
          </Label>
        </SimpleTooltip>
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <Input
            id="callerExitDestination"
            value={formData.callerExitDestination}
            onChange={(event) => onFieldChange('callerExitDestination', event.target.value)}
            placeholder={t('queueProfiles.form.fields.callerExitDestination.placeholder')}
          />
        </div>

        {/* Ringback Tone */}
        <SimpleTooltip content={t('queueProfiles.form.fields.ringbackTone.tooltip')} className="col-span-12 md:col-span-6 lg:col-span-3">
          <Label htmlFor="ringbackTone" className="text-left">
            {t('queueProfiles.form.fields.ringbackTone.label')}
          </Label>
        </SimpleTooltip>
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <Input
            id="ringbackTone"
            value={formData.ringbackTone}
            onChange={(event) => onFieldChange('ringbackTone', event.target.value)}
            placeholder={t('queueProfiles.form.fields.ringbackTone.placeholder')}
          />
        </div>

        {/* Announce Wrap-up Message */}
        <SimpleTooltip content={t('queueProfiles.form.fields.announceWrapUpMessage.tooltip')} className="col-span-12 md:col-span-6 lg:col-span-3">
          <Label htmlFor="announceWrapUpMessage" className="text-left">
            {t('queueProfiles.form.fields.announceWrapUpMessage.label')}
          </Label>
        </SimpleTooltip>
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <Input
            id="announceWrapUpMessage"
            value={formData.announceWrapUpMessage}
            onChange={(event) => onFieldChange('announceWrapUpMessage', event.target.value)}
            placeholder={t('queueProfiles.form.fields.announceWrapUpMessage.placeholder')}
          />
        </div>

        {/* Announce Resume Playback */}
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <SimpleTooltip content={t('queueProfiles.form.fields.announceResumePlayback.tooltip')}>
            <Label htmlFor="announceResumePlayback" className="text-left">
              {t('queueProfiles.form.fields.announceResumePlayback.label')}
            </Label>
          </SimpleTooltip>
        </div>
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <Toggle
            id="announceResumePlayback"
            value={formData.announceResumePlayback}
            onChange={(value) => onFieldChange('announceResumePlayback', value)}
          />
        </div>

        {/* Play Ringback Before MOH */}
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <SimpleTooltip content={t('queueProfiles.form.fields.playRingbackBeforeMOH.tooltip')}>
            <Label htmlFor="playRingbackBeforeMOH" className="text-left">
              {t('queueProfiles.form.fields.playRingbackBeforeMOH.label')}
            </Label>
          </SimpleTooltip>
        </div>
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <Toggle
            id="playRingbackBeforeMOH"
            value={formData.playRingbackBeforeMOH}
            onChange={(value) => onFieldChange('playRingbackBeforeMOH', value)}
          />
        </div>

        {/* Agent Whisper Tone */}
        <SimpleTooltip content={t('queueProfiles.form.fields.agentWhisperTone.tooltip')} className="col-span-12 md:col-span-6 lg:col-span-3">
          <Label htmlFor="agentWhisperTone" className="text-left">
            {t('queueProfiles.form.fields.agentWhisperTone.label')}
          </Label>
        </SimpleTooltip>
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <Input
            id="agentWhisperTone"
            value={formData.agentWhisperTone}
            onChange={(event) => onFieldChange('agentWhisperTone', event.target.value)}
            placeholder={t('queueProfiles.form.fields.agentWhisperTone.placeholder')}
          />
        </div>
      </div>
    </FormBox>
  );
}

